import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Chip, Separator, Typography } from '@heroui/react';
import { Icon } from '@iconify/react';
import type { SalesTransaction } from '@/api/models';
import { formatGel, getNodeTestIdProp, getNodeTestProps } from '@/utils';
import { saleDiscountTag } from '../../transactionsMeta';

type TransactionDetailTotalsProps = {
    transaction: SalesTransaction;
};

/**
 * The money breakdown of the transaction detail (render-only): Subtotal,
 * Line discounts (only when above zero), the Sale discount with its tag and
 * note (only when there is one), Fees (when any) and the Total. Discount
 * figures are in the default foreground (the warning-soft colour fails
 * contrast in dark themes); the minus sign, the labels and the tag carry the
 * meaning. A sale without discounts shows Subtotal and Total
 * as it always has.
 */
export const TransactionDetailTotals: React.FC<
    TransactionDetailTotalsProps
> = ({ transaction }) => {
    const { t } = useTranslation('pages');
    const { cartDiscount } = transaction;

    return (
        <div className="flex flex-col gap-1.5">
            <div className="flex items-center justify-between">
                <Typography color="muted" type="body-sm">
                    {t('transactions.modal.subtotal')}
                </Typography>
                <Typography className="tabular-nums" type="body-sm">
                    {formatGel(transaction.subtotal)}
                </Typography>
            </div>

            {transaction.lineDiscountTotal > 0 ? (
                <div
                    className="flex items-center justify-between"
                    {...getNodeTestIdProp('transaction-detail-line-discounts')}
                >
                    <Typography type="body-sm">
                        {t('transactions.modal.lineDiscounts')}
                    </Typography>
                    <Typography className="tabular-nums" type="body-sm">
                        {`−${formatGel(transaction.lineDiscountTotal)}`}
                    </Typography>
                </div>
            ) : null}

            {cartDiscount ? (
                <div
                    className="flex items-start justify-between gap-3"
                    {...getNodeTestIdProp('transaction-detail-sale-discount')}
                >
                    <div className="flex min-w-0 flex-col">
                        <div className="flex items-center gap-2">
                            <Typography type="body-sm">
                                {t('transactions.modal.discount')}
                            </Typography>
                            <Chip color="warning" size="sm" variant="primary">
                                {saleDiscountTag(cartDiscount)}
                            </Chip>
                        </div>
                        {cartDiscount.note ? (
                            <div className="flex items-center gap-1.5 text-muted">
                                <Icon
                                    className="size-3"
                                    icon="solar:notes-linear"
                                />
                                <Typography color="muted" type="body-xs">
                                    {t('transactions.modal.note', {
                                        note: cartDiscount.note,
                                    })}
                                </Typography>
                            </div>
                        ) : null}
                    </div>
                    <Typography
                        className="shrink-0 tabular-nums"
                        type="body-sm"
                    >
                        {`−${formatGel(cartDiscount.amount)}`}
                    </Typography>
                </div>
            ) : null}

            {transaction.fees > 0 ? (
                <div className="flex items-center justify-between">
                    <Typography color="muted" type="body-sm">
                        {t('transactions.modal.fees')}
                    </Typography>
                    <Typography className="tabular-nums" type="body-sm">
                        {formatGel(transaction.fees)}
                    </Typography>
                </div>
            ) : null}

            <Separator className="my-1" />
            <div className="flex items-center justify-between">
                <Typography weight="semibold">
                    {t('transactions.modal.total')}
                </Typography>
                <Typography
                    className="tabular-nums leading-none"
                    {...getNodeTestProps('transaction-detail-total')}
                    type="h5"
                    weight="bold"
                >
                    {formatGel(transaction.total)}
                </Typography>
            </div>
        </div>
    );
};
